import React, { useContext, useState, useEffect, useMemo, useCallback, createContext, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks'
import { parseBackendDate, convertToBackendStr, addDay } from 'services/dates'
import { ViewTypeContext, EDITOR_ROUTE_ENUM } from 'components/Campaign'
import _ from 'lodash'
import { showSnackBar } from 'services/toast'
import { generateUuid } from 'services/uuid'
import { submitCampaign, publishCampaign, CAMPAIGN_STATUS_ENUM, selectEditingCampaign } from 'state/campaigns'
import { TaskState, StepState } from 'state/tasks'
import { selectRegistrationDate } from 'state/registrations'
import {
    EXTRA_PAYOUT_SCHEMA,
    STEP_DATA_SCHEMAS,
    STEP_TIME_WINDOW_SCHEMA,
    TASK_TITLE_SCHEMA,
    PAYOUTS_SCHEMA,
    SCHEMA_TIME_WINDOW_END_TIME_MUST_BE_N_HOURS_LATER_THAN_PREV_STEP,
} from '../validationSchema'
import * as yup from 'yup'
import OnFormikValidationError from 'components/OnFormikValidationError'
import { Formik, Form } from 'formik'
import {
    COUNTRY_ENUM,
    StepTypes,
    TaskTypes,
    TASK_TYPE_ENUM,
    STEP_TYPE_ENUM,
    TasksWithEngagementStep,
} from 'services/api/constants'
import { getLinkInBioPostStepType, hasLinkInBioStepType } from 'components/utils'
import SocialMediaTaskEditor from './SocialMediaTaskEditor'
import { TaskSections } from '..'
import SaveAndSubmitButtonGroup from 'components/Campaign/Editor/SaveAndSubmitButtonGroup'
import { selectTaskErrors } from 'state/error'
import {
    runValidateSchema,
    validateStringOrNumberArrayField,
    validateFieldsAreAllPassed,
    setInitialTouched,
} from 'components/utils'
import { ERROR_MESSAGES } from '../errorMessages'
import AttendEventPurchaseTaskEditor from './AttendEventPurchaseTaskEditor'
import MaskWrapper from 'components/Campaign/Editor/MaskWrapper'
import CompareChangesPopup, { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import EditCampaignRestricted from 'components/Shared/Permission/EditCampaignRestricted'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'
import { usePermission } from 'hooks/permissions'
import { selectTaskPlatformContentTypesByName } from 'state/constants'
import { SelectedEngagementConfig } from './PayoutEditor'
import { selectIsExternalUser } from 'state/profile'
import { useStepEditorInitialValues } from 'hooks/steps'

interface EditorValues extends Omit<TaskState, 'steps'> {
    stepData: any
    payouts: any
}

interface EditorData {
    getEditor: (requiredFieldNames: { [key: string]: string[] }) => JSX.Element
}
interface PredefinedTaskEditorProps {
    taskSettings: TaskTypes
    stepSettings: _.Dictionary<StepTypes>
    task: TaskState
    saveTask: (task: TaskState, idle_hours_to_submit_engagement?: number, successCallback?: () => void) => void
    selectedTaskSection: TaskSections
    campaignBrandCountry: COUNTRY_ENUM | null
    uuid: string
}
interface DynamicTaskStateProps {
    includedSteps: { [stepType: string]: boolean }
    additionalPayoutIds: string[]
}
interface PredefinedContextProps {
    isCampaignLive: boolean
    linkInBioPostStepType: string | undefined
    task: TaskState
    allStepsInTask: STEP_TYPE_ENUM[]
    stepSettings: _.Dictionary<StepTypes>
    dynamicTaskState: DynamicTaskStateProps
    showSchedulePostTime: boolean
    isTaskWithEngagementStep: boolean
    addAdditionalPayout: () => string
    taskSettings: TaskTypes
    removeAdditionalPayout: (id: string) => void
    campaignBrandCountry: COUNTRY_ENUM | null
}
export const PredefinedContext = createContext({} as PredefinedContextProps)

export enum EDITOR_TYPE_ENUM {
    FB_IG_EDITOR = 'fbIgEditor',
    TIKTOK_EDITOR = 'tiktokEditor',
}

const PredefinedTaskEditor: React.FC<PredefinedTaskEditorProps> = ({
    taskSettings,
    stepSettings,
    task,
    saveTask,
    selectedTaskSection,
    campaignBrandCountry,
    uuid,
}) => {
    const [t] = useNewTranslation()
    const { steps, ...nonStepTaskDetails } = task
    const [dynamicTaskState, setDynamicTaskState] = useState<DynamicTaskStateProps>({
        includedSteps: {},
        additionalPayoutIds: [],
    })
    const isPublish = useRef(true)
    const [currentValue, setValue] = useState<any>(null)
    const dispatch = useDispatch()
    const viewType = useContext(ViewTypeContext)
    const campaign = useSelector(selectEditingCampaign)
    const campaignStatus = campaign ? campaign.status : undefined
    const isCampaignLive = campaignStatus === CAMPAIGN_STATUS_ENUM.LIVE
    const stepsByType = _.keyBy(steps, 'type')
    const stepsInTask = Object.keys(stepsByType)
    const allStepsInTask = steps.map(step => step.type)
    const taskPlatformContentTypesByName = useSelector(selectTaskPlatformContentTypesByName)
    const engagementTypes = useMemo(() => {
        return task?.platform_content_type
            ? taskPlatformContentTypesByName[task?.platform_content_type].engagement_types
            : []
    }, [taskPlatformContentTypesByName, task])
    const engagementPayoutTypes = taskSettings.config.payout_types
        .filter(payoutType => payoutType.is_engagement)
        .map(payoutType => payoutType.id)
    const idle_hours_to_submit_engagement = taskSettings.config.idle_hours_to_submit_engagement
    const isTaskWithEngagementStep = TasksWithEngagementStep.includes(task.type)
    const linkInBioPostStepType = getLinkInBioPostStepType(task.type)
    // use this toggle to avoid state not correct reflect in formikBag
    const canPublishCampaign =
        (campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        usePermission(CampaignStatusControl.LaunchCampaign)
    const taskError = useSelector(selectTaskErrors(task.uuid))
    const mainViewRef = useRef<HTMLDivElement>(null)
    const isExternalUser = useSelector(selectIsExternalUser)
    const registrationDate = useSelector(selectRegistrationDate)
    const initialValues = useStepEditorInitialValues(campaign, task)
    const isAbleToReview =
        usePermission(CampaignStatusControl.ReviewCampaign) && campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW
    const isAbleToLaunch =
        (campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        usePermission(CampaignStatusControl.LaunchCampaign)
    if (!campaign) return <></>

    useEffect(() => {
        const includedStepsState = allStepsInTask.reduce((state: any, stepType: string) => {
            state[stepType] = _.includes(stepsInTask, stepType)
            return state
        }, {})
        // To set submit proof photo is included as false if data is for display only
        if ([TASK_TYPE_ENUM.ATTEND_EVENT, TASK_TYPE_ENUM.PURCHASE_PRODUCT].includes(taskSettings.name)) {
            const isSubmitPhotoTaskMock = task.steps.findIndex((stepTask: StepState) => stepTask.uuid === '__mock__')
            if (isSubmitPhotoTaskMock > -1) {
                includedStepsState[STEP_TYPE_ENUM.SUBMIT_PROOF_PHOTO] = false
            }
        }
        let additionalPayoutIds: string[] = []
        if (nonStepTaskDetails) {
            additionalPayoutIds = Object.keys(
                nonStepTaskDetails.payouts.extras ? nonStepTaskDetails.payouts.extras : {},
            )
        }
        setDynamicTaskState({
            additionalPayoutIds,
            includedSteps: includedStepsState,
        })
    }, [taskSettings, task])

    useEffect(() => {
        if (mainViewRef && mainViewRef.current) {
            mainViewRef.current.scrollTop = 0
        }
    }, [selectedTaskSection])

    const toggleIncludedStep = (stepName: string, isIncluded: boolean): void => {
        dynamicTaskState.includedSteps[stepName] = isIncluded
        const state = Object.assign({}, dynamicTaskState.includedSteps, { [stepName]: isIncluded })
        setDynamicTaskState({
            additionalPayoutIds: dynamicTaskState.additionalPayoutIds,
            includedSteps: state,
        })
    }

    const addAdditionalPayout = (): string => {
        const uuid = generateUuid()
        setDynamicTaskState({
            ...dynamicTaskState,
            additionalPayoutIds: [...dynamicTaskState.additionalPayoutIds, uuid],
        })
        return uuid
    }

    const removeAdditionalPayout = (idToRemove: string): void => {
        setDynamicTaskState({
            ...dynamicTaskState,
            additionalPayoutIds: dynamicTaskState.additionalPayoutIds.filter((id: string) => id !== idToRemove),
        })
    }

    const getEditorData = (taskType: TASK_TYPE_ENUM): EditorData => {
        switch (taskType) {
            case TASK_TYPE_ENUM.ATTEND_EVENT:
            case TASK_TYPE_ENUM.PURCHASE_PRODUCT: {
                return {
                    getEditor(requiredFieldNames): JSX.Element {
                        return (
                            <MaskWrapper>
                                <CampaignEditorInputFieldContainer>
                                    <AttendEventPurchaseTaskEditor
                                        task={task}
                                        selectedTaskSection={selectedTaskSection}
                                        requiredFieldNames={requiredFieldNames}
                                        includedSteps={dynamicTaskState.includedSteps}
                                        toggleIncludedStep={toggleIncludedStep}
                                    />
                                </CampaignEditorInputFieldContainer>
                            </MaskWrapper>
                        )
                    },
                }
            }
            case TASK_TYPE_ENUM.IG_POST:
            case TASK_TYPE_ENUM.IG_POST_V02:
            case TASK_TYPE_ENUM.TIKTOK_POST:
            case TASK_TYPE_ENUM.FB_POST:
            case TASK_TYPE_ENUM.FB_POST_V02:
            case TASK_TYPE_ENUM.IG_STORY:
            case TASK_TYPE_ENUM.IG_STORY_V02:
            case TASK_TYPE_ENUM.IG_REEL:
            case TASK_TYPE_ENUM.XIAOHONGSHU_POST:
                return {
                    getEditor(requiredFieldNames): JSX.Element {
                        return (
                            <MaskWrapper>
                                <SocialMediaTaskEditor
                                    selectedTaskSection={selectedTaskSection}
                                    task={task}
                                    requiredFieldNames={requiredFieldNames}
                                    engagementTypes={engagementTypes}
                                />
                            </MaskWrapper>
                        )
                    },
                }
            default: {
                return {
                    getEditor(): JSX.Element {
                        return <></>
                    },
                }
            }
        }
    }
    const onTaskSuccessUpdateCallback = (): void => {
        if (canPublishCampaign) {
            dispatch(
                //launch campaign
                publishCampaign({
                    data: {
                        campaign: {} as any,
                        uuid: uuid,
                    },
                }),
            )
        } else {
            dispatch(
                //submit to pending
                submitCampaign({
                    data: {
                        campaign: {} as any,
                        uuid: uuid,
                    },
                }),
            )
        }
    }

    let showSchedulePostTime = false
    if (
        task.type === TASK_TYPE_ENUM.FB_POST ||
        task.type === TASK_TYPE_ENUM.FB_POST_V02 ||
        task.type === TASK_TYPE_ENUM.IG_POST ||
        task.type === TASK_TYPE_ENUM.IG_POST_V02 ||
        task.type === TASK_TYPE_ENUM.IG_STORY ||
        task.type === TASK_TYPE_ENUM.IG_STORY_V02 ||
        task.type === TASK_TYPE_ENUM.TIKTOK_POST ||
        task.type === TASK_TYPE_ENUM.IG_REEL ||
        task.type === TASK_TYPE_ENUM.XIAOHONGSHU_POST
    ) {
        showSchedulePostTime = true
    }

    const editorData = useMemo(() => getEditorData(task.type), [task, selectedTaskSection])

    const { validationSchema, requiredFieldNames } = useMemo(() => {
        const isCampaignDraftOrForEdit =
            campaignStatus === CAMPAIGN_STATUS_ENUM.DRAFT || campaignStatus === CAMPAIGN_STATUS_ENUM.FOR_EDIT
        const getBaseValidationSchema = (): any => {
            const payoutsSchema = taskSettings.config.payout_types.reduce((memo: any, payoutType) => {
                const hasMinMax = !payoutType.no_counter
                const hasEngegement = payoutType.is_engagement
                const schema = {} as any
                const payoutSchema = PAYOUTS_SCHEMA(isCampaignDraftOrForEdit)
                schema.cost = payoutSchema.cost
                if (hasMinMax) {
                    schema.min = payoutSchema.min
                    schema.max = payoutSchema.max
                }
                if (hasEngegement) {
                    memo[payoutType.id] = yup.object().when('engagementMetrics', {
                        is: engagementMetrics => {
                            return engagementMetrics
                                ? engagementMetrics.some((metric: { value: string }) => metric.value === payoutType.id)
                                : false
                        },
                        then: yup.object().shape({ ...schema }),
                    })
                } else {
                    memo[payoutType.id] = yup.object().shape({ ...schema })
                }
                return memo
            }, {})
            return yup.object().shape({
                payouts: yup.object({
                    ...payoutsSchema,
                    extras: EXTRA_PAYOUT_SCHEMA(isCampaignDraftOrForEdit),
                    engagementMetrics: yup.array().nullable(),
                }),
            })
        }
        const getTaskValidationSchema = (): any => {
            const baseValidationSchema = getBaseValidationSchema()
            const registrationStartDate = registrationDate.start
                ? parseBackendDate(registrationDate.start, campaign.country)
                : null
            const registrationEndDate = registrationDate.end
                ? parseBackendDate(registrationDate.end, campaign.country, {
                      showTime: false,
                      showYear: true,
                      addSeconds: -1,
                  })
                : null
            const stepsData = {
                ...allStepsInTask.reduce(
                    (memo: any, stepType: STEP_TYPE_ENUM) => {
                        if (dynamicTaskState.includedSteps[stepType] === false) return memo

                        const fieldsConfig = stepSettings[stepType].config.step.fields
                        const fieldNames = Object.keys(stepSettings[stepType].config.step.fields)
                        const currentStepIndex = taskSettings.config.steps.indexOf(stepType)
                        const previousStep =
                            currentStepIndex > 0 ? taskSettings.config.steps[currentStepIndex - 1] : null
                        const requiredFieldNames = fieldNames.filter(
                            fieldName => fieldsConfig[fieldName] && (fieldsConfig[fieldName] as any).required !== false,
                        )
                        const dataSchema = fieldNames.reduce(
                            (schema: { [key: string]: yup.Schema<any> }, fieldName: string) => {
                                const stepSchema = STEP_DATA_SCHEMAS(isCampaignDraftOrForEdit)
                                const fieldSchema = stepSchema[
                                    fieldName as keyof typeof stepSchema
                                ] as yup.ArraySchema<any>
                                if (!fieldSchema) return schema

                                schema[fieldName] = fieldSchema
                                const fieldConfig = fieldsConfig[fieldName] as any

                                if (fieldConfig.required === false) {
                                    schema[fieldName] = fieldSchema.notRequired()
                                } else if (fieldConfig.type === 'list' && fieldConfig.required === false) {
                                    schema[fieldName] = fieldSchema.min(0).notRequired()
                                }
                                return schema
                            },
                            {},
                        )

                        memo.requiredFieldNames[stepType] = requiredFieldNames
                        memo.stepDataSchema[stepType] = yup.object({
                            data: yup.object().shape(dataSchema),
                            time_window: STEP_TIME_WINDOW_SCHEMA(
                                campaign,
                                initialValues,
                                isExternalUser,
                                previousStep,
                                {
                                    start: registrationStartDate,
                                    end: registrationEndDate,
                                    isRegistrationTimeComparison: false,
                                },
                                isPublish,
                            ),
                        })
                        if (
                            [
                                STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_FACEBOOK_POST,
                                STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_POST,
                                STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_INSTAGRAM_REEL,
                                STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_SCREENSHOT,
                                STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_TIKTOK_POST,
                                STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_XIAOHONGSHU_POST,
                            ].includes(stepType) &&
                            idle_hours_to_submit_engagement
                        ) {
                            memo.stepDataSchema[stepType] = memo.stepDataSchema[stepType].concat(
                                yup.object().shape({
                                    time_window: SCHEMA_TIME_WINDOW_END_TIME_MUST_BE_N_HOURS_LATER_THAN_PREV_STEP(
                                        previousStep,
                                        idle_hours_to_submit_engagement,
                                    ),
                                }),
                            )
                        }

                        return memo
                    },
                    {
                        requiredFieldNames: {},
                        stepDataSchema: {},
                    },
                ),
            }

            return {
                validationSchema: baseValidationSchema.shape({
                    title: TASK_TITLE_SCHEMA(isCampaignDraftOrForEdit),
                    stepData: yup.object(stepsData.stepDataSchema),
                }),
                requiredFieldNames: stepsData.requiredFieldNames,
            }
        }
        return getTaskValidationSchema()
    }, [
        campaign,
        taskSettings,
        stepSettings,
        dynamicTaskState,
        initialValues,
        isExternalUser,
        idle_hours_to_submit_engagement,
        registrationDate,
    ])

    const formErrors = useMemo(() => {
        const returnError: Record<string, any> = {}
        if (!taskError) {
            return returnError
        } else {
            for (const key in taskError) {
                if (key === 'title') {
                    const message = ERROR_MESSAGES[key][taskError[key]] || taskError[key]
                    returnError[key] = message
                }

                if (key === 'payouts') {
                    const error = taskError[key]
                    let payoutsError: any = {}
                    if (returnError[key] && typeof returnError[key] === 'object' && !Array.isArray(returnError[key])) {
                        payoutsError = Object.assign({}, returnError[key])
                    }
                    if (error['base']) {
                        payoutsError['base'] = {}
                        const code = error['base']
                        if (
                            currentValue &&
                            currentValue[key] &&
                            currentValue[key]['base'] &&
                            currentValue[key]['base']['cost'] &&
                            !code
                        ) {
                            payoutsError['base']['cost'] = ''
                        } else {
                            const message = ERROR_MESSAGES[key]['base'][code] || code
                            payoutsError['base']['cost'] = message
                        }
                    }
                    if (error['extras']) {
                        if (currentValue && currentValue['payouts'] && currentValue['payouts']['extras']) {
                            const extrasErrors: any = _.cloneDeep(currentValue['payouts']['extras'])
                            for (const uuid in extrasErrors) {
                                const extraPayoutError = error['extras'][uuid]
                                if (extraPayoutError) {
                                    for (const key in extraPayoutError) {
                                        const message =
                                            ERROR_MESSAGES['payouts'][key][extraPayoutError[key]] ||
                                            extraPayoutError[key]
                                        extraPayoutError[key] = message
                                    }
                                    extrasErrors[uuid] = extraPayoutError
                                } else {
                                    delete extrasErrors[uuid]
                                }
                            }
                            payoutsError['extras'] = extrasErrors
                        }
                    }
                    returnError['payouts'] = payoutsError
                }
                if (key === 'steps') {
                    const stepsError = taskError[key]
                    const returnStepsError: any = {}
                    for (const taskId in stepsError) {
                        const stepTypeData = Object.values(stepsByType).find(step => step.uuid === taskId)
                        if (stepTypeData) {
                            const errors = stepsError[taskId]
                            if (hasLinkInBioStepType(stepTypeData.type) && taskError['data']) {
                                if (taskError['data']['link_in_bio_duration']) {
                                    errors['data']['link_in_bio_duration'] = taskError['data']['link_in_bio_duration']
                                }
                                if (taskError['data']['link_in_bio_url']) {
                                    errors['data']['link_in_bio_url'] = taskError['data']['link_in_bio_url']
                                }
                            }
                            if (errors && errors['engagement_types']) {
                                const code = errors['engagement_types']
                                let message = ''
                                if (code) {
                                    message = ERROR_MESSAGES['engagementMetrics'][code] || code
                                }
                                if (!returnError['payouts']) {
                                    returnError['payouts'] = {}
                                }
                                returnError['payouts']['engagementMetrics'] = message
                            }

                            if (errors) {
                                if (errors['data']) {
                                    Object.keys(errors['data']).forEach((error: string) => {
                                        const code = errors['data'][error]
                                        errors['data'][error] = ERROR_MESSAGES['steps'][error][code] || code
                                    })
                                }
                                if (errors['time_window']) {
                                    Object.keys(errors['time_window']).forEach((key: string) => {
                                        const code = errors['time_window'][key]
                                        errors['time_window'][key] = ERROR_MESSAGES['time_window'][key][code] || code
                                    })
                                }
                            }
                            returnStepsError[stepTypeData.type] = errors
                        }
                    }
                    returnError['stepData'] = returnStepsError
                }
            }
        }

        return returnError
    }, [taskError, task, currentValue, stepsByType])

    const initialTouched = useMemo(() => {
        const errors = _.cloneDeep(formErrors)
        return setInitialTouched(errors)
    }, [formErrors, taskError])

    const validate = useCallback(
        async values => {
            const schemaErrors = await runValidateSchema(values, validationSchema)
            const isInitialError = Object.keys(formErrors).length > 0
            if (isInitialError && isPublish.current) {
                const returnError = _.cloneDeep(formErrors)
                const stepsData = values.stepData
                if (returnError['title'] && values.title) {
                    if (schemaErrors && schemaErrors['title']) {
                        returnError['title'] = schemaErrors['title']
                    } else {
                        returnError['title'] = ''
                    }
                }
                if (returnError['stepData'] && stepsData) {
                    const stepDataSchemaError =
                        schemaErrors && schemaErrors['stepData'] ? schemaErrors['stepData'] : null
                    Object.keys(returnError['stepData']).forEach((key: string) => {
                        const stepSchemaError = stepDataSchemaError && stepDataSchemaError[key]
                        const stepData = stepsData && stepsData[key]
                        Object.keys(returnError['stepData'][key]).forEach((field: string) => {
                            const fieldSchemaError = stepSchemaError && stepSchemaError[field]
                            switch (field) {
                                case 'data': {
                                    Object.keys(returnError['stepData'][key][field]).forEach((subField: string) => {
                                        if (
                                            [
                                                'title',
                                                'requirement_text',
                                                'description',
                                                'submission_requirement_description',
                                                'link_in_bio_duration',
                                                'link_in_bio_url',
                                                'event_name',
                                                'event_information',
                                                'product_name',
                                            ].includes(subField)
                                        ) {
                                            if (fieldSchemaError && fieldSchemaError[subField]) {
                                                returnError['stepData'][key][field][subField] =
                                                    fieldSchemaError[subField]
                                            } else if (stepData[field][subField]) {
                                                returnError['stepData'][key][field][subField] = ''
                                            }
                                        }
                                        if (subField === 'extra_data') {
                                            let extra_require_pass = true
                                            if (
                                                Array.isArray(stepData[field][subField]) &&
                                                stepData[field][subField].length > 0
                                            ) {
                                                stepData[field][subField].forEach(
                                                    (value: { title: string; description: string }) => {
                                                        if (!value.title || !value.description) {
                                                            extra_require_pass = false
                                                        }
                                                    },
                                                )
                                            }
                                            if (extra_require_pass) {
                                                returnError['stepData'][key][field][subField] = ''
                                            }
                                        }
                                        if (
                                            [
                                                'media_requirement_dos',
                                                'media_requirement_donts',
                                                'caption_requirement_hashtags',
                                                'caption_requirement_dos',
                                                'caption_requirement_donts',
                                                'media_requirement_tags',
                                                'things_to_note',
                                                'how_to_buy',
                                            ].includes(subField)
                                        ) {
                                            if (stepData[field][subField] && Array.isArray(stepData[field][subField])) {
                                                const media_require_pass = validateStringOrNumberArrayField(
                                                    stepData[field][subField],
                                                )

                                                if (media_require_pass) {
                                                    returnError['stepData'][key][field][subField] = ''
                                                }
                                            }
                                        }
                                    })
                                    break
                                }
                                case 'time_window': {
                                    for (const step in returnError['stepData'][key][field]) {
                                        if (fieldSchemaError && fieldSchemaError[step]) {
                                            returnError['stepData'][key]['time_window'][step] = fieldSchemaError[step]
                                        } else if (stepData && stepData[field] && stepData[field][step]) {
                                            returnError['stepData'][key]['time_window'][step] = ''
                                        }
                                    }
                                    break
                                }
                                case 'engagement_types': {
                                    if (values['payouts']['engagementMetrics'].length > 0) {
                                        returnError['stepData'][key]['engagement_types'] = ''
                                        returnError['payouts']['engagementMetrics'] = ''
                                    }
                                }
                            }
                        })
                    })
                }

                if (taskError && taskError['payouts'] && returnError['payouts'] && values['payouts']) {
                    if (
                        values['payouts']['base'] &&
                        returnError['payouts']['base'] &&
                        returnError['payouts']['base']['cost']
                    ) {
                        if (values['payouts']['base']['cost'] > 0) {
                            returnError['payouts']['base']['cost'] = ''
                        } else {
                            returnError['payouts']['base']['cost'] =
                                ERROR_MESSAGES['payouts']['base'][taskError['payouts']['base']]
                        }
                    }
                    if (returnError['payouts']['extras']) {
                        if (values && values['payouts'] && values['payouts']['extras']) {
                            const newExtraError: Record<string, any> | string = {}
                            if (Object.keys(values['payouts']['extras']).length === 0) {
                                returnError['payouts']['extras'] = ''
                            }
                            for (const uuid in returnError['payouts']['extras']) {
                                const error: Record<string, string> = {
                                    cost: '',
                                    description: '',
                                    max: '',
                                    min: '',
                                    title: '',
                                }
                                for (const key in values['payouts']['extras'][uuid]) {
                                    if (
                                        values['payouts']['extras'][uuid][key] &&
                                        Boolean(values['payouts']['extras'][uuid][key])
                                    ) {
                                        error[key] = ''
                                    } else {
                                        error[key] = formErrors['payouts']['extras'][uuid][key]
                                    }
                                    newExtraError[uuid] = error
                                }
                                returnError['payouts']['extras'] = newExtraError
                            }
                        }
                    }
                }
                const isAllFieldPass = validateFieldsAreAllPassed(returnError)
                if (isAllFieldPass) {
                    return {}
                } else {
                    return returnError
                }
            } else {
                if (schemaErrors) {
                    return schemaErrors
                } else {
                    return {}
                }
            }
        },
        [formErrors, task, taskError, validationSchema],
    )

    useEffect(() => {
        setValue(initialValues)
    }, [initialValues])

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues as EditorValues}
            initialErrors={formErrors}
            initialTouched={initialTouched}
            validate={validate}
            onSubmit={(values: EditorValues, actions): void => {
                setValue(values)
                const { payouts, stepData, ...originalValues } = values
                const selectedEngagementValues =
                    payouts && payouts.engagementMetrics
                        ? payouts.engagementMetrics.map(
                              (engagementMetric: SelectedEngagementConfig) => engagementMetric.value,
                          )
                        : []
                const requestBody = Object.assign({}, originalValues, {
                    steps: taskSettings.config.steps.map((stepType: string) => {
                        if (!stepData.hasOwnProperty(stepType)) return null
                        if (dynamicTaskState.includedSteps[stepType] === false) return null
                        const startTime = stepData[stepType]?.time_window?.start
                        const endTime = stepData[stepType]?.time_window?.end
                        const parsedStepData = {
                            ...stepData[stepType],
                            time_window: {
                                start: startTime ? convertToBackendStr(startTime, campaign.country) : null,
                                end: endTime ? convertToBackendStr(addDay(endTime, 1), campaign.country) : null,
                            },
                        }
                        return parsedStepData
                    }),
                    payouts: Object.keys(payouts).reduce((memo: any, payoutKey: string) => {
                        const isValidNoneExtraPayoutKey =
                            payoutKey !== 'extras' &&
                            payoutKey !== 'engagementMetrics' && // used for engagement checkbox, no need to includes in payout
                            (!engagementPayoutTypes.includes(payoutKey) || selectedEngagementValues.includes(payoutKey))

                        if (payoutKey !== 'extras') {
                            if (isValidNoneExtraPayoutKey) {
                                memo[payoutKey] = payouts[payoutKey]
                            }
                        } else {
                            memo.extras = Object.keys(payouts.extras).reduce((m: any, extraKey: string) => {
                                if (dynamicTaskState.additionalPayoutIds.indexOf(extraKey) >= 0) {
                                    m[extraKey] = payouts.extras[extraKey]
                                }
                                return m
                            }, {})
                        }
                        return memo
                    }, {}),
                })
                // can not get latest link_in_bio_type in submit function, current work around is replace data at here
                requestBody.steps.forEach((step: any, index: number) => {
                    if (step && originalValues && 'link_in_bio_type' in step.data && originalValues.data) {
                        // todo
                        // eslint-disable-next-line @typescript-eslint/no-extra-semi
                        ;(requestBody.steps[index] as any).data.link_in_bio_type = originalValues.data.link_in_bio_type
                    }
                })
                requestBody.steps = _.compact(requestBody.steps)
                //
                if (isPublish.current) {
                    saveTask(
                        _.omitBy(requestBody, _.isNil) as TaskState,
                        idle_hours_to_submit_engagement,
                        onTaskSuccessUpdateCallback,
                    )
                } else {
                    saveTask(_.omitBy(requestBody, _.isNil) as TaskState, idle_hours_to_submit_engagement)
                }

                actions.setSubmitting(false)
            }}
        >
            {(formikBag): JSX.Element => {
                return (
                    <CompareChangesPopup
                        initialData={formikBag.initialValues}
                        compareData={formikBag.values}
                        recordChangesKey={RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE}
                    >
                        <Form>
                            <OnFormikValidationError
                                callback={(): void => {
                                    showSnackBar({
                                        title: t('errorMessage.validation.enterDetailsRedSubtext'),
                                        type: 'error',
                                    })
                                }}
                            />
                            <div ref={mainViewRef} className={'form-field'}>
                                <PredefinedContext.Provider
                                    value={{
                                        isCampaignLive,
                                        linkInBioPostStepType,
                                        task,
                                        allStepsInTask,
                                        stepSettings,
                                        dynamicTaskState,
                                        showSchedulePostTime,
                                        isTaskWithEngagementStep,
                                        addAdditionalPayout,
                                        taskSettings,
                                        removeAdditionalPayout,
                                        campaignBrandCountry,
                                    }}
                                >
                                    {editorData.getEditor(requiredFieldNames)}
                                </PredefinedContext.Provider>
                            </div>

                            {viewType === EDITOR_ROUTE_ENUM.EDIT && (
                                <EditCampaignRestricted
                                    campaignStatus={campaignStatus}
                                    isEditDeadline={selectedTaskSection === TaskSections.SCHEDULE}
                                    isAbleToReview={isAbleToReview}
                                    isAbleToLaunch={isAbleToLaunch}
                                >
                                    <SaveAndSubmitButtonGroup
                                        campaign={campaign}
                                        disableSave={false}
                                        disableSubmit={!formikBag.isValid}
                                        onSubmit={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                            e.preventDefault()
                                            isPublish.current = true
                                            formikBag.submitForm()
                                        }}
                                        onSave={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                            e.preventDefault()
                                            isPublish.current = false
                                            formikBag.submitForm()
                                        }}
                                    />
                                </EditCampaignRestricted>
                            )}
                        </Form>
                    </CompareChangesPopup>
                )
            }}
        </Formik>
    )
}

export default PredefinedTaskEditor
